/*
 * Thème Wamii « Studio » — script écrit de zéro pour Wamii (ADR 0297). Aucune bibliothèque.
 * Mouvement : apparitions au défilement, en-tête qui se rétracte, menus dépliants, diaporamas en fondu, récit image/texte, parallaxe, galerie en fondu.
 * Commerce : variantes, panier coulissant (API Ajax de Shopify + rendu de section), recherche instantanée, produits liés.
 * Tout fonctionne sans script (formulaires classiques) ; « prefers-reduced-motion » coupe les animations, jamais les fonctions.
 */
(() => {
  const racine = document.documentElement;
  racine.classList.remove('no-js');
  const calme = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Mouvement (réglage du thème, ADR 0368) : aucun, sobre (le mouvement d'origine du thème), cinéma (plus ample, titres mot à mot, images dévoilées).
  // Navigateur automatisé (capture « vrai rendu », vignettes) : pas d'animation, toute la page visible d'emblée.
  const niveau = document.body.dataset.wsMouvement || 'sobre';
  const animations = niveau !== 'aucun' && !calme && !navigator.webdriver;
  const cinema = animations && niveau === 'cinema';
  if (animations) racine.classList.add('ws-anime');
  if (cinema) racine.classList.add('ws-mvt-cinema');
  const routes = (window.Shopify && window.Shopify.routes) || { root: '/' };
  const url = (chemin) => `${routes.root.replace(/\/$/, '')}${chemin}`;
  const $$ = (sel, base = document) => [...base.querySelectorAll(sel)];
  const lireSection = (html, sel) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector(sel);
  };

  /* ---------- Cinéma : titres révélés mot à mot ----------
     Seulement un titre de texte simple (aucune balise dedans) de 15 mots au plus ; jamais dans l'éditeur de thème (le texte y est modifié en direct). */
  const titresMots = () => {
    if (!cinema || (window.Shopify && window.Shopify.designMode)) return;
    $$(':is(h1, h2)[data-ws-apparition]:not([data-ws-mots]), [data-ws-apparition] :is(h1, h2):not([data-ws-mots])').forEach((t) => {
      if (t.children.length > 0) return;
      const mots = t.textContent.trim().split(/\s+/);
      if (mots.length === 0 || mots.length > 15 || mots[0] === '') return;
      t.setAttribute('data-ws-mots', '');
      // Premier écran : les mots montent sans jamais être transparents (l'affichage n'est pas retardé).
      if (t.getBoundingClientRect().top < window.innerHeight * 0.85) t.setAttribute('data-ws-premier', '');
      t.textContent = '';
      mots.forEach((m, j) => {
        const span = document.createElement('span');
        span.className = 'ws-mot';
        span.style.setProperty('--j', j);
        span.textContent = m;
        t.append(span, j < mots.length - 1 ? ' ' : '');
      });
    });
  };

  /* ---------- Apparitions au défilement ---------- */
  const apparitions = () => {
    titresMots();
    const elements = $$('[data-ws-apparition]:not(.est-visible)');
    if (!animations || !('IntersectionObserver' in window)) { elements.forEach((e) => e.classList.add('est-visible')); return; }
    const obs = new IntersectionObserver((entrees) => {
      entrees.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('est-visible'); obs.unobserve(e.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    elements.forEach((e) => obs.observe(e));
  };

  /* ---------- En-tête : se rétracte en descendant, revient en remontant ---------- */
  const entete = () => {
    const bloc = document.querySelector('.section-wamii-entete');
    if (!bloc) return;
    let dernier = window.scrollY;
    let attente = false;
    const majour = () => {
      const y = window.scrollY;
      const ouvert = bloc.querySelector('details[open]');
      bloc.classList.toggle('est-defile', y > 24);
      if (!ouvert && bloc.dataset.wsMasquer !== 'non') bloc.classList.toggle('est-cache', y > 320 && y > dernier);
      if (y < dernier) bloc.classList.remove('est-cache');
      dernier = y;
      attente = false;
    };
    window.addEventListener('scroll', () => { if (!attente) { attente = true; requestAnimationFrame(majour); } }, { passive: true });
    majour();
  };

  /* ---------- Menus dépliants (ordinateur : au survol ; partout : au clic, Échap, clic dehors) ---------- */
  const menus = () => {
    const survol = window.matchMedia('(hover: hover) and (min-width: 990px)');
    $$('[data-ws-depliant]').forEach((d) => {
      let minuteur;
      d.addEventListener('mouseenter', () => { if (!survol.matches) return; clearTimeout(minuteur); $$('[data-ws-depliant][open]').forEach((o) => o !== d && o.removeAttribute('open')); d.setAttribute('open', ''); });
      d.addEventListener('mouseleave', () => { if (!survol.matches) return; minuteur = setTimeout(() => d.removeAttribute('open'), 160); });
      d.addEventListener('toggle', () => { if (d.open) $$('[data-ws-depliant][open]').forEach((o) => o !== d && o.removeAttribute('open')); });
    });
    document.addEventListener('click', (e) => { $$('[data-ws-depliant][open]').forEach((d) => { if (!d.contains(e.target)) d.removeAttribute('open'); }); });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      $$('details[open][data-ws-depliant], details[open].ws-tiroir, details[open].ws-recherche').forEach((d) => { d.removeAttribute('open'); d.querySelector('summary')?.focus(); });
      fermerPanier();
    });
    // Tiroir mobile : bloque le défilement de la page tant qu'il est ouvert.
    $$('.ws-tiroir').forEach((t) => t.addEventListener('toggle', () => racine.classList.toggle('ws-sans-defilement', t.open)));
  };

  /* ---------- Diaporamas en fondu (héros) ---------- */
  const diaporamas = () => {
    $$('[data-ws-diaporama]').forEach((d) => {
      const diapos = $$('[data-ws-diapo]', d);
      if (diapos.length < 2) { diapos[0]?.classList.add('est-active'); return; }
      const points = $$('[data-ws-point]', d);
      const duree = Math.max(3, Number(d.dataset.wsDuree) || 6) * 1000;
      d.style.setProperty('--ws-duree', `${duree}ms`);
      let actuelle = 0;
      let minuteur;
      let pause = false;
      const aller = (n) => {
        diapos[actuelle].classList.remove('est-active');
        points[actuelle]?.removeAttribute('aria-current');
        actuelle = (n + diapos.length) % diapos.length;
        diapos[actuelle].classList.add('est-active');
        points[actuelle]?.setAttribute('aria-current', 'true');
        d.style.setProperty('--ws-teinte', diapos[actuelle].dataset.wsTeinte || '');
        relancer();
      };
      const relancer = () => { clearTimeout(minuteur); if (!pause && animations) minuteur = setTimeout(() => aller(actuelle + 1), duree); };
      points.forEach((p, i) => p.addEventListener('click', () => aller(i)));
      d.querySelector('[data-ws-precedente]')?.addEventListener('click', () => aller(actuelle - 1));
      d.querySelector('[data-ws-suivante]')?.addEventListener('click', () => aller(actuelle + 1));
      const suspendre = (oui) => { pause = oui; d.classList.toggle('est-en-pause', oui); relancer(); };
      d.addEventListener('mouseenter', () => suspendre(true));
      d.addEventListener('mouseleave', () => suspendre(false));
      d.addEventListener('focusin', () => suspendre(true));
      d.addEventListener('focusout', () => suspendre(false));
      document.addEventListener('visibilitychange', () => suspendre(document.hidden));
      let depart = null;
      d.addEventListener('touchstart', (e) => { depart = e.touches[0].clientX; }, { passive: true });
      d.addEventListener('touchend', (e) => { if (depart === null) return; const dx = e.changedTouches[0].clientX - depart; if (Math.abs(dx) > 40) aller(actuelle + (dx < 0 ? 1 : -1)); depart = null; });
      aller(0);
    });
  };

  /* ---------- Récit : l'image change en fondu quand le texte défile ---------- */
  const recits = () => {
    $$('[data-ws-recit]').forEach((r) => {
      const etapes = $$('[data-ws-etape]', r);
      const images = $$('[data-ws-recit-image]', r);
      const activer = (i) => {
        images.forEach((img, j) => img.classList.toggle('est-active', j === i));
        etapes.forEach((e, j) => e.classList.toggle('est-active', j === i));
        r.style.setProperty('--ws-teinte', etapes[i]?.dataset.wsTeinte || '');
      };
      activer(0);
      if (!('IntersectionObserver' in window)) return;
      const obs = new IntersectionObserver((entrees) => {
        entrees.forEach((e) => { if (e.isIntersecting) activer(etapes.indexOf(e.target)); });
      }, { rootMargin: '-45% 0px -45% 0px' });
      etapes.forEach((e) => obs.observe(e));
    });
  };

  /* ---------- Parallaxe douce ---------- */
  const parallaxes = () => {
    if (!animations) return;
    const elements = $$('[data-ws-parallaxe]');
    if (elements.length === 0) return;
    // Cinéma : profondeur plus marquée sur ordinateur, toujours dans la marge de l'image agrandie (12 %).
    const ampleur = cinema && window.matchMedia('(min-width: 990px)').matches ? -10 : -8;
    let attente = false;
    const majour = () => {
      const h = window.innerHeight;
      elements.forEach((el) => {
        const boite = el.parentElement.getBoundingClientRect();
        if (boite.bottom < 0 || boite.top > h) return;
        const progres = (boite.top + boite.height / 2 - h / 2) / h;
        const decalage = cinema ? Math.max(-5.5, Math.min(5.5, progres * ampleur)) : progres * ampleur;
        el.style.transform = `translate3d(0, ${decalage.toFixed(2)}%, 0) scale(1.12)`;
      });
      attente = false;
    };
    window.addEventListener('scroll', () => { if (!attente) { attente = true; requestAnimationFrame(majour); } }, { passive: true });
    majour();
  };

  /* ---------- Carrousels horizontaux (flèches) ---------- */
  const carrousels = () => {
    $$('[data-ws-carrousel]').forEach((c) => {
      const piste = c.querySelector('[data-ws-piste]');
      if (!piste) return;
      const pas = () => piste.clientWidth * 0.85;
      c.querySelector('[data-ws-gauche]')?.addEventListener('click', () => piste.scrollBy({ left: -pas(), behavior: calme ? 'auto' : 'smooth' }));
      c.querySelector('[data-ws-droite]')?.addEventListener('click', () => piste.scrollBy({ left: pas(), behavior: calme ? 'auto' : 'smooth' }));
    });
  };

  /* ---------- Page produit : galerie en fondu, variantes, barre d'achat qui suit ---------- */
  const produits = () => {
    $$('[data-ws-produit]').forEach((bloc) => {
      const medias = $$('[data-ws-media]', bloc);
      const vignettes = $$('[data-ws-vignette]', bloc);
      let actif = 0;
      const montrer = (i) => {
        if (medias.length === 0) return;
        actif = (i + medias.length) % medias.length;
        medias.forEach((m, j) => m.classList.toggle('est-actif', j === actif));
        vignettes.forEach((v, j) => v.setAttribute('aria-current', String(j === actif)));
        vignettes[actif]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: calme ? 'auto' : 'smooth' });
      };
      vignettes.forEach((v, i) => v.addEventListener('click', () => montrer(i)));
      bloc.querySelector('[data-ws-media-prec]')?.addEventListener('click', () => montrer(actif - 1));
      bloc.querySelector('[data-ws-media-suiv]')?.addEventListener('click', () => montrer(actif + 1));
      const scene = bloc.querySelector('[data-ws-scene]');
      let depart = null;
      scene?.addEventListener('touchstart', (e) => { depart = e.touches[0].clientX; }, { passive: true });
      scene?.addEventListener('touchend', (e) => { if (depart === null) return; const dx = e.changedTouches[0].clientX - depart; if (Math.abs(dx) > 40) montrer(actif + (dx < 0 ? 1 : -1)); depart = null; });

      const donnees = bloc.querySelector('script[type="application/json"][data-ws-variantes]');
      const variantes = donnees ? JSON.parse(donnees.textContent || '[]') : [];
      const selects = $$('select[data-ws-option]', bloc);
      const groupes = $$('[data-ws-groupe]', bloc);
      const champId = bloc.querySelector('form input[name="id"]');
      const prix = $$('[data-ws-prix]', bloc);
      const boutons = $$('button[name="add"]', bloc);
      // Choix courant : les listes déroulantes, sinon les pastilles (une par option).
      const lireChoix = () => (groupes.length > 0 ? groupes.map((g) => g.querySelector('input:checked')?.value ?? '') : selects.map((s) => s.value));
      const majour = () => {
        const choix = lireChoix();
        groupes.forEach((g, i) => { const b = g.querySelector('[data-ws-choisi]'); if (b) b.textContent = choix[i] ?? ''; });
        const v = variantes.find((x) => x.options.every((o, i) => o === choix[i]));
        boutons.forEach((b) => { b.disabled = !v || !v.disponible; b.textContent = !v ? 'Indisponible' : v.disponible ? 'Ajouter au panier' : 'Épuisé'; });
        if (!v) return;
        if (champId) champId.value = String(v.id);
        prix.forEach((p) => { p.innerHTML = v.prix; });
        if (v.media) { const i = medias.findIndex((m) => m.dataset.wsMedia === String(v.media)); if (i >= 0) montrer(i); }
        const adresse = new URL(window.location.href); adresse.searchParams.set('variant', String(v.id)); window.history.replaceState({}, '', adresse);
      };
      selects.forEach((s) => s.addEventListener('change', majour));
      $$('[data-ws-option-radio]', bloc).forEach((r) => r.addEventListener('change', majour));
      const quantite = bloc.querySelector('[data-ws-quantite] input');
      bloc.querySelector('[data-ws-moins]')?.addEventListener('click', () => { if (quantite) quantite.value = String(Math.max(1, (Number(quantite.value) || 1) - 1)); });
      bloc.querySelector('[data-ws-plus]')?.addEventListener('click', () => { if (quantite) quantite.value = String((Number(quantite.value) || 1) + 1); });

      const barre = document.querySelector('[data-ws-barre-achat]');
      const principal = bloc.querySelector('.ws-produit__achat');
      if (barre && principal && 'IntersectionObserver' in window) {
        new IntersectionObserver(([e]) => barre.classList.toggle('est-visible', !e.isIntersecting && e.boundingClientRect.top < 0)).observe(principal);
        barre.querySelector('button')?.addEventListener('click', () => bloc.querySelector('form[action*="/cart/add"] button[name="add"]')?.click());
      }
    });
  };

  /* ---------- Produits liés (recommandations de Shopify, chargées à l'approche) ---------- */
  const lies = () => {
    $$('[data-ws-lies]').forEach((b) => {
      const charger = async () => {
        try {
          const r = await fetch(b.dataset.wsLies);
          const contenu = lireSection(await r.text(), '[data-ws-lies]');
          if (contenu && contenu.innerHTML.trim() !== '') { b.innerHTML = contenu.innerHTML; apparitions(); carrousels(); } else { b.closest('.shopify-section')?.remove(); }
        } catch { /* sans réseau : la section reste vide */ }
      };
      if (!('IntersectionObserver' in window)) { charger(); return; }
      const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { obs.disconnect(); charger(); } }, { rootMargin: '400px' });
      obs.observe(b);
    });
  };

  /* ---------- Panier coulissant ---------- */
  const SECTION_PANIER = 'wamii-panier-tiroir';
  const panier = () => document.querySelector('[data-ws-panier]');
  let dernierFocus = null;
  const ouvrirPanier = () => {
    const p = panier(); if (!p) return;
    dernierFocus = document.activeElement;
    p.classList.add('est-ouvert'); p.setAttribute('aria-hidden', 'false');
    racine.classList.add('ws-sans-defilement');
    p.querySelector('[data-ws-panier-fermer]')?.focus();
  };
  function fermerPanier() {
    const p = panier(); if (!p || !p.classList.contains('est-ouvert')) return;
    p.classList.remove('est-ouvert'); p.setAttribute('aria-hidden', 'true');
    racine.classList.remove('ws-sans-defilement');
    dernierFocus?.focus?.();
  }
  const remplacerPanier = (sections) => {
    const html = sections && sections[SECTION_PANIER];
    const p = panier();
    if (!html || !p) return;
    const neuf = lireSection(html, '[data-ws-panier-contenu]');
    const cible = p.querySelector('[data-ws-panier-contenu]');
    if (neuf && cible) cible.innerHTML = neuf.innerHTML;
    const total = neuf?.dataset.wsTotalArticles;
    if (total !== undefined) $$('[data-ws-compteur]').forEach((c) => { c.textContent = total; c.hidden = total === '0'; });
  };
  const panierAjax = () => {
    if (!panier()) return;
    document.addEventListener('submit', async (e) => {
      const f = e.target;
      if (!(f instanceof HTMLFormElement) || !/\/cart\/add/.test(f.getAttribute('action') || '')) return;
      e.preventDefault();
      const bouton = f.querySelector('button[name="add"]');
      const erreur = f.querySelector('[data-ws-erreur]');
      if (erreur) erreur.hidden = true;
      bouton?.classList.add('est-en-cours');
      try {
        const donnees = new FormData(f);
        donnees.append('sections', SECTION_PANIER);
        donnees.append('sections_url', window.location.pathname);
        const r = await fetch(url('/cart/add.js'), { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: donnees });
        const reponse = await r.json();
        if (!r.ok) { if (erreur) { erreur.textContent = reponse.description || reponse.message || 'Ajout impossible.'; erreur.hidden = false; } return; }
        remplacerPanier(reponse.sections);
        ouvrirPanier();
      } catch { f.submit(); } finally { bouton?.classList.remove('est-en-cours'); }
    });
    document.addEventListener('click', async (e) => {
      const cible = e.target instanceof Element ? e.target : null;
      if (!cible) return;
      if (cible.closest('[data-ws-ouvrir-panier]') && !document.body.classList.contains('gabarit-cart')) { e.preventDefault(); ouvrirPanier(); return; }
      if (cible.closest('[data-ws-panier-fermer]') || cible.matches('[data-ws-panier-voile]')) { fermerPanier(); return; }
      const ligne = cible.closest('[data-ws-ligne]');
      const action = cible.closest('[data-ws-qte]');
      if (!ligne || !action) return;
      e.preventDefault();
      const actuelle = Number(ligne.dataset.wsQuantite) || 0;
      const quantite = action.dataset.wsQte === 'retirer' ? 0 : Math.max(0, actuelle + Number(action.dataset.wsQte));
      ligne.classList.add('est-en-cours');
      try {
        const r = await fetch(url('/cart/change.js'), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: ligne.dataset.wsLigne, quantity: quantite, sections: SECTION_PANIER, sections_url: window.location.pathname }) });
        const reponse = await r.json();
        if (r.ok) remplacerPanier(reponse.sections); else ligne.classList.remove('est-en-cours');
      } catch { ligne.classList.remove('est-en-cours'); }
    });
  };

  /* ---------- Recherche instantanée (et par rayon, en-tête « Grand magasin ») ---------- */
  // Les produits d'un rayon : lus une fois par l'API officielle de rendu de section, gardés pour la visite (rien d'autre n'est lu ni gardé).
  const HANDLE = /^[a-z0-9][a-z0-9-]{0,254}$/;
  const rayons = new Map();
  const membresDuRayon = (handle) => {
    if (!HANDLE.test(handle || '')) return Promise.resolve(null);
    if (!rayons.has(handle)) {
      rayons.set(handle, (async () => {
        const cle = `ws-rayon:${handle}`;
        try { const garde = sessionStorage.getItem(cle); if (garde) { const o = JSON.parse(garde); return { titre: o.titre, produits: new Set(o.produits) }; } } catch {}
        const r = await fetch(`${url(`/collections/${handle}`)}?section_id=wamii-rayon-produits`);
        if (!r.ok) return null;
        const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
        const o = JSON.parse(doc.querySelector('[data-ws-rayon]')?.textContent || 'null');
        if (!o || !Array.isArray(o.produits)) return null;
        try { sessionStorage.setItem(cle, JSON.stringify(o)); } catch {}
        return { titre: String(o.titre || ''), produits: new Set(o.produits) };
      })().catch(() => null));
    }
    return rayons.get(handle);
  };
  const filtrerParRayon = (racine, membres) => {
    let gardes = 0;
    racine.querySelectorAll('li').forEach((li) => {
      const h = li.getAttribute('data-ws-produit');
      const garde = h !== null && membres.produits.has(h);
      li.style.display = garde ? '' : 'none';
      if (garde) gardes++;
    });
    return gardes;
  };
  const recherche = () => {
    $$('[data-ws-recherche]').forEach((boite) => {
      const champ = boite.querySelector('input[name="q"]');
      const resultats = boite.querySelector('[data-ws-suggestions]');
      const choix = boite.querySelector('[data-ws-rayon-choix]');
      const details = boite.closest('details');
      details?.addEventListener('toggle', () => { if (details.open) setTimeout(() => champ?.focus(), 60); });
      if (!champ || !resultats) return;
      // Sur la page de résultats, le rayon choisi reste choisi.
      const rayonDeLaPage = new URLSearchParams(location.search).get('ws_rayon');
      if (choix && rayonDeLaPage && [...choix.options].some((o) => o.value === rayonDeLaPage)) choix.value = rayonDeLaPage;
      let minuteur;
      let derniere = '';
      const chercher = (force) => {
        clearTimeout(minuteur);
        const q = champ.value.trim();
        const rayon = choix?.value || '';
        if (q.length < 2) { resultats.innerHTML = ''; derniere = ''; return; }
        minuteur = setTimeout(async () => {
          const cle = `${rayon}|${q}`;
          if (cle === derniere && !force) return;
          derniere = cle;
          try {
            const [r, membres] = await Promise.all([
              fetch(`${url('/search/suggest')}?q=${encodeURIComponent(q)}&section_id=wamii-suggestions&resources[type]=product,collection,page&resources[limit]=${rayon ? 10 : 6}&resources[options][unavailable_products]=last`),
              rayon ? membresDuRayon(rayon) : Promise.resolve(null),
            ]);
            const contenu = lireSection(await r.text(), '[data-ws-suggestions]');
            if (cle !== derniere || !contenu) return;
            if (rayon && membres) {
              const liste = contenu.querySelector('.ws-suggestions ul');
              const gardes = liste ? filtrerParRayon(liste, membres) : 0;
              const titre = contenu.querySelector('.ws-suggestions h3');
              if (titre) titre.textContent = `Dans le rayon ${membres.titre}`;
              if (gardes === 0 && liste) liste.insertAdjacentHTML('afterend', `<p style="opacity: .7">Aucun produit du rayon ${membres.titre.replace(/[<>&"]/g, '')} pour cette recherche.</p>`);
              const tous = contenu.querySelector('[data-ws-tous-resultats]');
              if (tous) tous.setAttribute('href', `${tous.getAttribute('href')}&ws_rayon=${encodeURIComponent(rayon)}`);
            }
            resultats.innerHTML = contenu.innerHTML;
          } catch { resultats.innerHTML = ''; }
        }, 220);
      };
      champ.addEventListener('input', () => chercher(false));
      choix?.addEventListener('change', () => chercher(true));
    });
    // Page de résultats : seulement les produits du rayon choisi, et le dire (avec le retour à tous les rayons).
    const grille = document.querySelector('[data-ws-resultats]');
    const note = document.querySelector('[data-ws-rayon-note]');
    const rayon = new URLSearchParams(location.search).get('ws_rayon');
    if (grille && note && rayon) {
      membresDuRayon(rayon).then((membres) => {
        if (!membres) return;
        const gardes = filtrerParRayon(grille, membres);
        const tous = new URL(location.href);
        tous.searchParams.delete('ws_rayon');
        note.textContent = gardes > 0 ? `Dans le rayon ${membres.titre} : ${gardes} produit${gardes > 1 ? 's' : ''} sur cette page. ` : `Aucun produit du rayon ${membres.titre} sur cette page. `;
        const lien = document.createElement('a');
        lien.className = 'ws-lien';
        lien.href = tous.pathname + tous.search;
        lien.textContent = 'Voir tous les rayons';
        note.append(lien);
        note.hidden = false;
      });
    }
  };

  /* ---------- Filtres de collection : envoi automatique ---------- */
  const filtres = () => {
    $$('[data-ws-filtres]').forEach((f) => {
      let minuteur;
      f.addEventListener('change', () => { clearTimeout(minuteur); minuteur = setTimeout(() => f.requestSubmit ? f.requestSubmit() : f.submit(), 350); });
    });
  };

  const demarrer = () => { apparitions(); entete(); menus(); diaporamas(); recits(); parallaxes(); carrousels(); produits(); lies(); panierAjax(); recherche(); filtres(); };
  demarrer();
  // Éditeur de thème : une section rechargée doit retrouver son mouvement.
  document.addEventListener('shopify:section:load', () => { apparitions(); diaporamas(); recits(); carrousels(); produits(); });
})();
